import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';

import { SnTeamsAPI, SnUsersAPI } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth';

import { LiveUsersServiceInterface } from '../interfaces';
import { SnLiveUser } from '../models';
import { SnLiveTrackingService } from './live-tracking.service';

@Injectable()
export class LiveUsersService implements LiveUsersServiceInterface, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private readonly _disabledTeams = new Set<string>();
  private readonly _forcedOffShiftUsers = new Set<string>();

  private readonly _activeUserList$ = new BehaviorSubject<SnLiveUser[]>([]);
  private readonly _disabledTeams$ = new BehaviorSubject<Set<string>>(new Set());
  private readonly _countUsers$ = new BehaviorSubject<number>(0);

  constructor(
    private readonly _liveTrackingService: SnLiveTrackingService,
    private readonly _teamsService: SnTeamsAPI,
    private readonly _currentUser: SnCurrentUser,
    private readonly _usersAPI: SnUsersAPI,
  ) {
    this._countUsers$.next(this._liveTrackingService.usersTracking.userCount);
    this._initializeDefaultActiveTeams();
    this._loadDisabledTeamsFromSettings();
    this._subscribeToUserChanges();
  }

  public get isAvailable(): boolean {
    return this._liveTrackingService.liveUsersIsAvailable;
  }

  public get userList(): SnLiveUser[] {
    return this._activeUserList$.value;
  }

  public get countUsers(): number {
    return this._liveTrackingService.usersTracking.userCount;
  }

  public get countActiveUser(): number {
    return this._activeUserList$.value.length;
  }

  public get activeUserList$(): Observable<SnLiveUser[]> {
    return this._activeUserList$.asObservable();
  }

  public get disabledTeams$(): Observable<Set<string>> {
    return this._disabledTeams$.asObservable();
  }

  public get countUsers$(): Observable<number> {
    return this._countUsers$.asObservable();
  }

  public get countActiveUser$(): Observable<number> {
    return this._activeUserList$.pipe(map((list) => list.length));
  }

  public teamIsActive(teamId: string): boolean {
    return !this._disabledTeams.has(teamId);
  }

  public toggleTeam(teamId: string): void {
    if (this.teamIsActive(teamId)) {
      this._disabledTeams.add(teamId);
    } else {
      this._disabledTeams.delete(teamId);
    }

    this._updateActiveUserList();
    this._notifyDisabledTeamsChanged();
    this._saveDisabledTeams();
  }

  public getActiveUserList(): SnLiveUser[] {
    return this._updateActiveUserList();
  }

  public initializeDefaultActiveTeams(): void {
    this._initializeDefaultActiveTeams();
  }

  public loadDisabledTeamsFromSettings(disabledTeams: string[]): void {
    if (!disabledTeams?.length) {
      return;
    }
    disabledTeams.forEach((teamId) => this._disabledTeams.add(teamId));
    this._notifyDisabledTeamsChanged();
    this._updateActiveUserList();
  }

  public updateActiveTeams(disabledTeams: string[]): Observable<void> {
    return this._currentUser
      .updateSettings({
        ...this._currentUser.settings,
        mapSetting: {
          ...this._currentUser.settings.mapSetting,
          disabledTeams: [...disabledTeams],
        },
      })
      .pipe(map(() => undefined));
  }

  public userIsOffShift(userId: string): Observable<void> {
    return this._usersAPI.updateSettings(userId, { isOnShift: false }).pipe(
      map(() => {
        const currentList = this._activeUserList$.value;
        const user = currentList.find((u) => u.id === userId);
        if (user) {
          this._forcedOffShiftUsers.add(userId);
          const updatedList = currentList.filter((u) => u.id !== userId);
          this._activeUserList$.next(updatedList);
          this._notifyCountUsersChanged();
        }
        return undefined;
      }),
    );
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _subscribeToUserChanges(): void {
    this._liveTrackingService.inited$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._updateActiveUserList();
      this._notifyCountUsersChanged();
    });

    this._liveTrackingService.usersTracking.userLoggedIn$
      .pipe(takeUntil(this._destroy$))
      .subscribe((user) => {
        if (user) {
          this._forcedOffShiftUsers.delete(user.id);
        }
        this._updateActiveUserList();
        this._notifyCountUsersChanged();
      });

    this._liveTrackingService.usersTracking.userLoggedOut$
      .pipe(takeUntil(this._destroy$))
      .subscribe((user) => {
        this._forcedOffShiftUsers.delete(user.id);
        const currentList = this._activeUserList$.value;
        const updatedList = currentList.filter((u) => u.id !== user.id);
        this._activeUserList$.next(updatedList);
        this._notifyCountUsersChanged();
      });
  }

  private _updateActiveUserList(): SnLiveUser[] {
    const userList = this._liveTrackingService.usersTracking.userList;
    const activeUserList = userList.filter(
      (user) =>
        !this._forcedOffShiftUsers.has(user.id) && (!user.team || this.teamIsActive(user.team?.id)),
    );

    this._activeUserList$.next(activeUserList);

    return activeUserList;
  }

  private _initializeDefaultActiveTeams(): void {
    const defaultActiveTeam = this._currentUser.settings.defaultMapTeamLayer;

    if (!defaultActiveTeam) {
      return;
    }

    this._teamsService
      .getTeamList({ pageSize: 'all' })
      .pipe(
        map(({ results }) => results.filter((team) => team.id !== defaultActiveTeam)),
        takeUntil(this._destroy$),
      )
      .subscribe((teams) => {
        teams.forEach((team) => this._disabledTeams.add(team.id));
        this._disabledTeams.delete(defaultActiveTeam);

        this._updateActiveUserList();
        this._notifyDisabledTeamsChanged();
      });
  }

  private _loadDisabledTeamsFromSettings(): void {
    const disabledTeams = this._currentUser.settings.mapSetting?.disabledTeams;
    if (!disabledTeams?.length) {
      return;
    }
    disabledTeams.forEach((teamId) => this._disabledTeams.add(teamId));
    this._notifyDisabledTeamsChanged();
    this._updateActiveUserList();
  }

  private _saveDisabledTeams(): void {
    this.updateActiveTeams([...this._disabledTeams.values()])
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  private _notifyDisabledTeamsChanged(): void {
    this._disabledTeams$.next(new Set(this._disabledTeams));
  }

  private _notifyCountUsersChanged(): void {
    this._countUsers$.next(this._liveTrackingService.usersTracking.userCount);
  }
}
